import { GameState, Layout, Theme, CAPACITY, TubeRow } from './types';
export type { Layout };
import { isTubeDone, COLOR_IDS } from './game';
import { t } from './i18n';

// Map a logical color id (game.ts COLOR_IDS) to its display color from the theme.
function displayColor(id: string, theme: Theme): string {
  const idx = COLOR_IDS.indexOf(id);
  return idx >= 0 ? theme.colors[idx] : id;
}

// ── Layout ────────────────────────────────────────────────────────────────

function rowLayout(
  tubeCount: number,
  tubeW: number,
  gap: number,
  W: number,
  centerY: number,
  tubeH: number,
): TubeRow {
  const totalW = tubeCount * tubeW + (tubeCount - 1) * gap;
  const startX = (W - totalW) / 2 + tubeW / 2;
  return {
    count: tubeCount,
    y: centerY - tubeH / 2,
    xs: Array.from({ length: tubeCount }, (_, i) => startX + i * (tubeW + gap)),
  };
}

export function computeLayout(W: number, H: number, tubeCount: number): Layout {
  const uiH = Math.max(52, H * 0.1);
  const available = { w: W, h: H - uiH - 16 };

  // Determine row split
  const rowCounts: number[] =
    tubeCount <= 5 ? [tubeCount] :
    tubeCount === 6 ? [3, 3] :
    tubeCount === 7 ? [4, 3] :
    [4, 4];

  const numRows = rowCounts.length;
  const maxPerRow = Math.max(...rowCounts);
  const rowGap = 20;
  const tubeGap = 12;

  // Compute max tube size that fits
  const tubeWFromW = (available.w - tubeGap * (maxPerRow - 1) - 16) / maxPerRow;
  const tubeHFromH = (available.h - rowGap * (numRows - 1) - 16) / numRows;
  const tubeW = Math.min(Math.floor(tubeWFromW), 80);
  const tubeH = Math.min(Math.floor(tubeHFromH), tubeW * 4.2);

  const cellH = tubeH / CAPACITY; // clip handles bottom rounding

  const totalRowsH = numRows * tubeH + (numRows - 1) * rowGap;
  const rowsStartY = uiH + 8 + (available.h - totalRowsH) / 2;

  const rows: TubeRow[] = rowCounts.map((count, ri) => {
    const centerY = rowsStartY + ri * (tubeH + rowGap) + tubeH / 2;
    return rowLayout(count, tubeW, tubeGap, W, centerY, tubeH);
  });

  return { tubeW, tubeH, cellH, rows, uiH, W, H };
}

// ── Hit testing ───────────────────────────────────────────────────────────

export function tubeAtPoint(x: number, y: number, layout: Layout): number | null {
  const { tubeW, tubeH, rows } = layout;
  let idx = 0;
  for (const row of rows) {
    for (let i = 0; i < row.count; i++) {
      const cx = row.xs[i];
      const ty = row.y;
      if (x >= cx - tubeW / 2 - 6 && x <= cx + tubeW / 2 + 6 &&
          y >= ty - 10 && y <= ty + tubeH + 10) {
        return idx;
      }
      idx++;
    }
  }
  return null;
}

// ── Flask path ────────────────────────────────────────────────────────────

function flaskPath(
  ctx: CanvasRenderingContext2D,
  cx: number, ty: number,
  tubeW: number, tubeH: number,
  open: boolean,
): void {
  const r  = tubeW / 2;
  const lx = cx - r;
  const rx = cx + r;
  const by = ty + tubeH;

  ctx.beginPath();
  if (open) {
    ctx.moveTo(lx, ty);
    ctx.lineTo(lx, by - r);
    ctx.arc(cx, by - r, r, Math.PI, 0, true);   // anticlockwise → bottom curve points down
    ctx.lineTo(rx, ty);
  } else {
    ctx.moveTo(lx, ty);
    ctx.lineTo(lx, by - r);
    ctx.arc(cx, by - r, r, Math.PI, 0, true);
    ctx.lineTo(rx, ty);
    ctx.closePath();
  }
}

// ── Draw a single liquid unit ─────────────────────────────────────────────

function drawLiquidUnit(
  ctx: CanvasRenderingContext2D,
  cx: number, unitY: number,
  tubeW: number, cellH: number,
  color: string,
  isBottom: boolean,
): void {
  const pad = 2;
  const lx  = cx - tubeW / 2 + pad;
  const w   = tubeW - pad * 2;
  const r   = tubeW / 2 - pad;

  if (isBottom) {
    const by = unitY + cellH;
    const br = tubeW / 2;
    ctx.beginPath();
    ctx.moveTo(lx, unitY);
    ctx.lineTo(lx, by - br + pad);
    ctx.arc(cx, by - br + pad, r, Math.PI, 0, true); // anticlockwise → bottom curve points down
    ctx.lineTo(lx + w, unitY);
    ctx.closePath();
  } else {
    ctx.beginPath();
    ctx.rect(lx, unitY, w, cellH);
  }
  ctx.fillStyle = color;
  ctx.fill();
}

// ── Draw tube with its liquid ─────────────────────────────────────────────

function drawTube(
  ctx: CanvasRenderingContext2D,
  cx: number, ty: number,
  layout: Layout,
  tubes: GameState['tubes'],
  tubeIdx: number,
  theme: Theme,
  selected: boolean,
  done: boolean,
  shakeOffsetX: number,
  animOffsetUnits: { color: string; offsetY: number; alpha: number }[],
  hideTopCount: number,
): void {
  const { tubeW, tubeH, cellH } = layout;
  const tube = tubes[tubeIdx];
  const drawCx = cx + shakeOffsetX;

  ctx.save();

  // Selection / done glow
  if (selected) {
    ctx.shadowColor = theme.tubeSelect;
    ctx.shadowBlur  = 18;
  } else if (done) {
    ctx.shadowColor = theme.tubeDone;
    ctx.shadowBlur  = 12;
  }

  // ── Liquid (clipped to flask interior) ───────────────────────────────
  ctx.save();
  flaskPath(ctx, drawCx, ty, tubeW, tubeH, false);
  ctx.clip();

  // Background fill inside flask
  ctx.fillStyle = theme.tubeBg;
  ctx.fillRect(drawCx - tubeW / 2, ty, tubeW, tubeH);

  const visibleCount = tube.length - hideTopCount;
  for (let i = 0; i < visibleCount; i++) {
    const unitY = ty + cellH * (CAPACITY - 1 - i);
    drawLiquidUnit(ctx, drawCx, unitY, tubeW, cellH, displayColor(tube[i], theme), i === 0);
  }

  // Animated units (with offset + alpha)
  for (const u of animOffsetUnits) {
    ctx.globalAlpha = u.alpha;
    // determine position: place on top of the base visible stack
    const baseIdx = visibleCount + animOffsetUnits.indexOf(u);
    const unitY   = ty + cellH * (CAPACITY - 1 - baseIdx) + u.offsetY;
    drawLiquidUnit(ctx, drawCx, unitY, tubeW, cellH, displayColor(u.color, theme), baseIdx === 0);
    ctx.globalAlpha = 1;
  }

  ctx.restore(); // end clip

  ctx.shadowBlur = 0;

  // ── Flask outline ─────────────────────────────────────────────────────
  flaskPath(ctx, drawCx, ty, tubeW, tubeH, true);
  ctx.strokeStyle = done ? theme.tubeDone : selected ? theme.tubeSelect : theme.tubeStroke;
  ctx.lineWidth   = done || selected ? 2.5 : 1.8;
  ctx.lineCap     = 'round';
  ctx.stroke();

  ctx.restore();
}

// ── Menu (☰ → overlay: Resume / Home) ────────────────────────────────────

export type Rect = { x: number; y: number; w: number; h: number };

export function menuRects(layout: Layout) {
  const { uiH, W, H } = layout;
  const restart = restartBtnCenter(layout);
  const toggle = { cx: restart.cx - restart.r * 2 - 10, cy: uiH / 2, r: restart.r };

  const bw = Math.min(260, W - 64);
  const bh = 52, gap = 14;
  const px = (W - bw) / 2;
  const total = bh * 2 + gap;
  const py = (H - total) / 2;
  const resume: Rect = { x: px, y: py, w: bw, h: bh };
  const hub:    Rect = { x: px, y: py + bh + gap, w: bw, h: bh };
  const panel:  Rect = { x: px - 20, y: py - 24, w: bw + 40, h: total + 48 };
  return { toggle, panel, resume, hub };
}

function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
}

function drawMenu(
  ctx: CanvasRenderingContext2D,
  W: number, H: number,
  layout: Layout,
  theme: Theme,
  menuOpen: boolean,
): void {
  const { toggle, panel, resume, hub } = menuRects(layout);

  drawRoundBtn(ctx, toggle.cx, toggle.cy, toggle.r, '☰', theme);

  if (!menuOpen) return;

  ctx.save();
  ctx.fillStyle = theme.overlayBg;
  ctx.fillRect(0, 0, W, H);

  roundRectPath(ctx, panel.x, panel.y, panel.w, panel.h, 18);
  ctx.fillStyle = theme.btnBg;
  ctx.fill();
  ctx.strokeStyle = theme.btnStroke;
  ctx.lineWidth = 1;
  ctx.stroke();

  const items: [Rect, string, boolean][] = [
    [resume, t('resume'), false],
    [hub,    t('to_hub'), true],   // акцентный
  ];
  for (const [r, label, accent] of items) {
    roundRectPath(ctx, r.x, r.y, r.w, r.h, 12);
    ctx.fillStyle = accent ? theme.tubeSelect : theme.btnBg;
    ctx.fill();
    ctx.strokeStyle = theme.btnStroke;
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle    = accent ? theme.bg : theme.textUi;
    ctx.font         = 'bold 17px -apple-system, sans-serif';
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2 + 1);
    ctx.textBaseline = 'alphabetic';
  }
  ctx.restore();
}

// ── UI helpers ────────────────────────────────────────────────────────────

function drawRoundBtn(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, r: number,
  label: string, theme: Theme,
  fontSize?: number,
): void {
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle   = theme.btnBg;
  ctx.fill();
  ctx.strokeStyle = theme.btnStroke;
  ctx.lineWidth   = 1;
  ctx.stroke();

  ctx.fillStyle    = theme.textUi;
  ctx.font         = `${fontSize ?? r * 0.85}px -apple-system, sans-serif`;
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, cx, cy);
  ctx.textBaseline = 'alphabetic';
}

// ── Main render ───────────────────────────────────────────────────────────

export function renderFrame(
  ctx: CanvasRenderingContext2D,
  W: number, H: number,
  layout: Layout,
  state: GameState,
  theme: Theme,
  nowMs: number,
  pendingStart: boolean,
  menuOpen: boolean,
): void {
  ctx.globalAlpha = 1;
  ctx.shadowBlur  = 0;

  // Background
  ctx.fillStyle = theme.bg;
  ctx.fillRect(0, 0, W, H);

  const { cellH, uiH, rows } = layout;

  // ── Compute animation state ───────────────────────────────────────────
  const anim = state.anim;
  let animProgress = 0;
  if (anim) {
    const raw = Math.min(1, (nowMs - anim.startMs) / anim.durationMs);
    // ease out: 1 - (1-p)²
    animProgress = 1 - (1 - raw) * (1 - raw);
  }

  // Squash/stretch scale for flying units
  const ssScale = anim ? 1 + 0.35 * Math.sin(Math.PI * animProgress) : 1;

  // ── Top UI bar ────────────────────────────────────────────────────────
  const btnR  = Math.min(uiH * 0.33, 22);
  const btnY  = uiH / 2;
  const btnPad = btnR + 14;

  // Undo button (left)
  drawRoundBtn(ctx, btnPad, btnY, btnR, t('undo'), theme);

  // Restart button (right)
  drawRoundBtn(ctx, W - btnPad, btnY, btnR, t('restart'), theme);

  // Menu toggle (top-right, left of restart) — clamp the centered level text
  // so it never reaches the toggle or the undo button on narrow viewports.
  const { toggle } = menuRects(layout);
  const levelTextMaxWidth = Math.max(20, 2 * Math.min(
    W / 2 - (btnPad + btnR),
    (toggle.cx - toggle.r) - W / 2,
  ) - 12);

  // Level indicator (center)
  ctx.fillStyle    = theme.textUi;
  ctx.font         = `600 ${Math.max(14, uiH * 0.32)}px -apple-system, sans-serif`;
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(t('level', { n: state.level + 1 }), W / 2, btnY, levelTextMaxWidth);
  ctx.textBaseline = 'alphabetic';

  // ── Tubes ─────────────────────────────────────────────────────────────
  let globalIdx = 0;
  for (const row of rows) {
    for (let i = 0; i < row.count; i++) {
      const cx   = row.xs[i];
      const ty   = row.y;
      const tube = state.tubes[globalIdx];
      const isSelected = state.selected === globalIdx;
      const isDone     = isTubeDone(tube);

      // Shake
      let shakeOffsetX = 0;
      if (state.shake && state.shake.idx === globalIdx) {
        const elapsed = nowMs - state.shake.startMs;
        const p = Math.min(elapsed / 300, 1);
        shakeOffsetX = Math.sin(elapsed * 0.065) * 5 * (1 - p);
      }

      // Selection lift: selected tube floats up slightly
      const liftY = isSelected ? -10 : 0;

      // Animation offsets for this tube
      let animOffsetUnits: { color: string; offsetY: number; alpha: number }[] = [];
      let hideTopCount = 0;

      if (anim) {
        if (globalIdx === anim.fromIdx) {
          // Source: poured units rise out of top
          hideTopCount = 0; // tube already has them removed in state
          // Show "ghost" of poured units rising upward
          animOffsetUnits = anim.colors.map((color, _ci) => ({
            color,
            offsetY: -cellH * animProgress * 2.5 * ssScale,
            alpha: Math.max(0, 1 - animProgress * 1.3),
          }));
        } else if (globalIdx === anim.toIdx) {
          // Destination: poured units fall in from above
          hideTopCount = anim.colors.length;
          const fallOffset = -cellH * (1 - animProgress) * 2.5;
          animOffsetUnits = anim.colors.map((color) => ({
            color,
            offsetY: fallOffset,
            alpha: Math.min(1, animProgress * 1.5),
          }));
        }
      }

      drawTube(
        ctx, cx, ty + liftY, layout,
        state.tubes, globalIdx,
        theme, isSelected, isDone,
        shakeOffsetX, animOffsetUnits, hideTopCount,
      );

      globalIdx++;
    }
  }

  // ── Menu (☰) ──────────────────────────────────────────────────────────
  if (state.phase === 'playing') {
    drawMenu(ctx, W, H, layout, theme, menuOpen);
  }

  // ── Win overlay ───────────────────────────────────────────────────────
  if (state.phase === 'won') {
    ctx.fillStyle = theme.overlayBg;
    ctx.fillRect(0, 0, W, H);

    ctx.textAlign = 'center';

    ctx.font = `bold ${Math.min(W * 0.18, 72)}px -apple-system, sans-serif`;
    ctx.fillStyle = theme.overlayText;
    ctx.fillText('🎉', W / 2, H / 2 - Math.min(W * 0.12, 50));

    const titleText   = t('level_done');   // endless pack — no final level
    ctx.font      = `bold ${Math.min(W * 0.075, 30)}px -apple-system, sans-serif`;
    ctx.fillStyle = theme.overlayText;
    ctx.fillText(titleText, W / 2, H / 2 + 4);

    const btnW  = Math.min(W * 0.55, 220);
    const btnH  = 48;
    const bx    = W / 2 - btnW / 2;
    const by2   = H / 2 + 28;
    const br2   = 12;
    ctx.beginPath();
    ctx.moveTo(bx + br2, by2);
    ctx.lineTo(bx + btnW - br2, by2);
    ctx.arcTo(bx + btnW, by2, bx + btnW, by2 + br2, br2);
    ctx.lineTo(bx + btnW, by2 + btnH - br2);
    ctx.arcTo(bx + btnW, by2 + btnH, bx + btnW - br2, by2 + btnH, br2);
    ctx.lineTo(bx + br2, by2 + btnH);
    ctx.arcTo(bx, by2 + btnH, bx, by2 + btnH - br2, br2);
    ctx.lineTo(bx, by2 + br2);
    ctx.arcTo(bx, by2, bx + br2, by2, br2);
    ctx.closePath();
    ctx.fillStyle = '#ffffff';
    ctx.fill();

    ctx.fillStyle    = '#111111';
    ctx.font         = `600 17px -apple-system, sans-serif`;
    ctx.textBaseline = 'middle';
    ctx.fillText(t('next_level'), W / 2, by2 + btnH / 2);
    ctx.textBaseline = 'alphabetic';
  }

  // ── Tap-to-start overlay ──────────────────────────────────────────────
  if (pendingStart) {
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(0, 0, W, H);
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle    = '#ffffff';
    ctx.font         = `bold ${Math.min(W * 0.055, 22)}px -apple-system, sans-serif`;
    ctx.fillText(t('tap_to_start'), W / 2, H / 2);
    ctx.textBaseline = 'alphabetic';
  }
}

// ── Hit test for overlay button ───────────────────────────────────────────
export function winBtnBounds(W: number, H: number): { x: number; y: number; w: number; h: number } {
  const btnW = Math.min(W * 0.55, 220);
  const btnH = 48;
  return { x: W / 2 - btnW / 2, y: H / 2 + 28, w: btnW, h: btnH };
}

export function undoBtnCenter(layout: Layout): { cx: number; cy: number; r: number } {
  const { uiH } = layout;
  const r   = Math.min(uiH * 0.33, 22);
  const btnPad = r + 14;
  return { cx: btnPad, cy: uiH / 2, r };
}

export function restartBtnCenter(layout: Layout): { cx: number; cy: number; r: number } {
  const { uiH, W } = layout;
  const r   = Math.min(uiH * 0.33, 22);
  const btnPad = r + 14;
  return { cx: W - btnPad, cy: uiH / 2, r };
}

// ── Theme ─────────────────────────────────────────────────────────────────
export function readTheme(): Theme {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string) => s.getPropertyValue(name).trim();
  return {
    bg:          v('--bg'),
    tubeBg:      v('--tube-bg'),
    tubeStroke:  v('--tube-stroke'),
    tubeSelect:  v('--tube-select'),
    tubeDone:    v('--tube-done'),
    textUi:      v('--text-ui'),
    btnBg:       v('--btn-bg'),
    btnStroke:   v('--btn-stroke'),
    overlayBg:   v('--overlay-bg'),
    overlayText: v('--overlay-text'),
    colors: Array.from({ length: 7 }, (_, i) => v(`--col-${i}`)),
  };
}
